import { set } from "lodash-es";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getUserConfig,
  getBannedWords,
  getParentConfig,
  getNotifications,
  getAllNotifications,
} from "app/api";
import {
  GameCase,
  EventType,
  Character,
  DialogType,
  ConfigState,
  CourseModel,
  ParentClaimStatus,
} from "app/types";
import i18n from "common/i18n";
import { insideInstalledApp } from "common/utils";
import { localeToHtmlLang } from "common/utils/locale";
import stamps from "./stamps.json";
import userIcons from "./user_icons.json";

export const getEnv = () => {
  if (insideInstalledApp()) {
    return "pwa";
  } else if ("ReactNativeWebView" in window) {
    if ("kidsAppPlatform" in window) {
      return window.kidsAppPlatform;
    } else {
      return "webview";
    }
  } else {
    return "web";
  }
};

const initialState: ConfigState = {
  loading: false,
  parentConfig: {
    is_first: true,
    locale: "ja",
    sound: { enable: true, volume: 0.5 },
    bgm: { enable: true, volume: 0.5 },
    tutorial: { top: true, creator_top: true, creator_panel: true },
    game_case: GameCase.GRAY,
    character: Character.DUCK,
    project_publish: true,
    project_copied: true,
    enable_lab: false,
    shake_bug_report: true,
    course_model: CourseModel.COMMAND,
    ai_chat: true,
    ai_generation: true,
  },
  userConfig: {
    is_first: true,
    locale: "ja",
    sound: { enable: true, volume: 0.5 },
    bgm: { enable: true, volume: 0.5 },
    tutorial: { top: true, creator_top: true, creator_panel: true },
    game_case: GameCase.GRAY,
    character: Character.DUCK,
    project_publish: true,
    project_copied: true,
    enable_lab: false,
    shake_bug_report: true,
    course_model: CourseModel.COMMAND,
    ai_chat: true,
    ai_generation: true,
  },
  tutorial: { top: true },
  notification: {
    kids: [],
    parents: [],
    maintenances: [],
  },
  banned: { words: [] },
  assets: Object.fromEntries(
    stamps.concat(userIcons).map((assets) => [assets.prefix, assets.contents]),
  ),
  event: { before_install_prompt: { value: false, args: {} } },
  dialog: {
    rating: { value: false, args: {} },
    contact: { value: false, args: {} },
    loading: { value: false, args: {} },
    feedback: { value: false, args: {} },
    assistant: { value: false, args: {} },
    universal: { value: false, args: {} },
    parent_gate: { value: false, args: {} },
    user_profile: { value: false, args: {} },
    notification: { value: false, args: {} },
    error_network: { value: false, args: {} },
    project_preview: { value: false, args: {} },
    pwa_install_prompt: { value: false, args: {} },
    upgrade_promotion: { value: false, args: {} },
    restricted_feature: { value: false, args: {} },
    parent_registration: { value: false, args: {} },
    parent_registration_prompt: { value: false, args: {} },
    shake_bug_report: { value: false, args: {} },
    parent_shake_bug_report: { value: false, args: {} },
  },
  justAuthenticated: false,
  lastClaimStatus: null,
};

const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    setLoading(state: ConfigState, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setJustAuthenticated(state: ConfigState, action: PayloadAction<boolean>) {
      state.justAuthenticated = action.payload;
    },
    setLastClaimStatus(state: ConfigState, action: PayloadAction<ParentClaimStatus | null>) {
      state.lastClaimStatus = action.payload;
    },
    updateParentConfig(
      state: ConfigState,
      action: PayloadAction<{ key: string; value: string | boolean | number }>,
    ) {
      const { key, value } = action.payload;
      set(state.parentConfig, key, value);
    },
    updateUserConfig(
      state: ConfigState,
      action: PayloadAction<{ key: string; value: string | boolean | number }>,
    ) {
      const { key, value } = action.payload;
      set(state.userConfig, key, value);
    },
    closeTutorial(state: ConfigState, action: PayloadAction<{ key: string }>) {
      state.tutorial[action.payload.key] = false;
    },
    clearDialog(state: ConfigState) {
      state.dialog = initialState.dialog;
    },
    updateDialog(
      state: ConfigState,
      action: PayloadAction<{
        type: DialogType;
        value: boolean;
        args?: { [key: string]: string };
      }>,
    ) {
      state.dialog[action.payload.type].value = action.payload.value;
      state.dialog[action.payload.type].args = action.payload.args;
    },
    updateEvent(
      state: ConfigState,
      action: PayloadAction<{
        type: EventType;
        value: boolean;
        args?: { [key: string]: string };
      }>,
    ) {
      state.event[action.payload.type].value = action.payload.value;
      state.event[action.payload.type].args = action.payload.args;
    },
  },
  extraReducers: (builder) => {
    builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(getParentConfig.fulfilled, (state: ConfigState, action: PayloadAction<any>) => {
        state.parentConfig = action.payload.user_config;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(getUserConfig.fulfilled, (state: ConfigState, action: PayloadAction<any>) => {
        state.userConfig = action.payload.user_config;
        i18n.locale = action.payload.user_config.locale;

        const rootElement = document.documentElement;
        rootElement.className = rootElement.className
          .replace(/\blang-[a-z]{2}(_[a-z]{2})?\b/g, "")
          .trim();
        if (state.userConfig.locale && !state.userConfig.locale.includes("ja")) {
          rootElement.classList.add(`lang-${state.userConfig.locale}`);
        }
        rootElement.lang = localeToHtmlLang(state.userConfig.locale);
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(getNotifications.fulfilled, (state: ConfigState, action: PayloadAction<any>) => {
        state.notification.kids = action.payload.kids;
        state.notification.parents = action.payload.parents;
        state.notification.maintenances = action.payload.maintenances;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(getAllNotifications.fulfilled, (state: ConfigState, action: PayloadAction<any>) => {
        state.notification.kids = action.payload.kids;
        state.notification.parents = action.payload.parents;
        state.notification.maintenances = action.payload.maintenances;
      })
      .addCase(
        getBannedWords.fulfilled,
        (state: ConfigState, action: PayloadAction<{ id: number; word: string }[]>) => {
          state.banned.words = action.payload;
        },
      );
  },
});

export const actions = { ...configSlice.actions };

export default configSlice.reducer;
